import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { embeddingProviderService, type EmbeddingProvider } from '../../../../components/api/embeddingProviderService';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  initialData?: EmbeddingProvider | null;
}

const AddEmbeddingProviderModal: React.FC<Props> = ({ onClose, onSubmit, initialData }) => {
  const isEdit = Boolean(initialData?.id);

  const [displayName, setDisplayName] = useState(initialData?.displayName ?? '');
  const [name, setName] = useState(initialData?.name ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [endpointUrl, setEndpointUrl] = useState(initialData?.endpointUrl ?? '');
  const [apiKey, setApiKey] = useState(initialData?.apiKey ?? '');
  const [defaultModel, setDefaultModel] = useState(initialData?.defaultModel ?? '');
  const [defaultVectorDimensions, setDefaultVectorDimensions] = useState<number | ''>(initialData?.defaultVectorDimensions ?? '');
  const [apiVersion, setApiVersion] = useState(initialData?.apiVersion ?? '');
  const [costPer1KTokens, setCostPer1KTokens] = useState<number | ''>(initialData?.costPer1KTokens ?? '');
  const [maxRequestsPerMinute, setMaxRequestsPerMinute] = useState<number | ''>(initialData?.maxRequestsPerMinute ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initialData?.websiteUrl ?? '');
  const [additionalConfigJson, setAdditionalConfigJson] = useState(initialData?.additionalConfigJson ?? '');
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const canSave = useMemo(() => {
    return Boolean(displayName.trim()) && Boolean(name.trim()) && Boolean(apiKey.trim());
  }, [displayName, name, apiKey]);

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const dto = {
        name: name.trim(),
        displayName: displayName.trim(),
        description: description || undefined,
        endpointUrl: endpointUrl || undefined,
        apiKey: apiKey || undefined,
        defaultModel: defaultModel || undefined,
        defaultVectorDimensions: defaultVectorDimensions === '' ? undefined : Number(defaultVectorDimensions),
        apiVersion: apiVersion || undefined,
        costPer1KTokens: costPer1KTokens === '' ? undefined : Number(costPer1KTokens),
        maxRequestsPerMinute: maxRequestsPerMinute === '' ? undefined : Number(maxRequestsPerMinute),
        websiteUrl: websiteUrl || undefined,
        additionalConfigJson: additionalConfigJson || undefined,
        isActive,
        isSystemProvider: initialData?.isSystemProvider ?? false,
      };

      if (isEdit && initialData?.id) {
        await embeddingProviderService.update(initialData.id, dto);
      } else {
        await embeddingProviderService.create(dto);
      }

      onSubmit();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to save embedding provider.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-neutral-900 w-full max-w-2xl rounded-xl shadow-xl border border-neutral-800 flex flex-col max-h-[90vh] my-auto">
        <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="text-white text-lg font-semibold">{isEdit ? 'Edit Embedding Provider' : 'Add New Embedding Provider'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" disabled={saving}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5 text-sm text-white">
          <div>
            <label className="text-gray-400 block mb-1">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Azure OpenAI Embeddings"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">Internal Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. AzureOpenAI"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Default Model</label>
              <input
                type="text"
                value={defaultModel}
                onChange={(e) => setDefaultModel(e.target.value)}
                placeholder="e.g. text-embedding-3-small"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Description (Optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief overview of this embedding provider"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Endpoint URL (Optional)</label>
            <input
              type="url"
              value={endpointUrl}
              onChange={(e) => setEndpointUrl(e.target.value)}
              placeholder="e.g. https://your-resource.openai.azure.com"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">Vector Dimensions</label>
              <input
                type="number"
                min={0}
                value={defaultVectorDimensions}
                onChange={(e) => setDefaultVectorDimensions(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="e.g. 1536"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">API Version (Optional)</label>
              <input
                type="text"
                value={apiVersion}
                onChange={(e) => setApiVersion(e.target.value)}
                placeholder="e.g. 2023-07-01-preview"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Cost per 1K Tokens (Optional)</label>
              <input
                type="number"
                min={0}
                step={0.0001}
                value={costPer1KTokens}
                onChange={(e) => setCostPer1KTokens(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="e.g. 0.0001"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-gray-400 hover:text-white text-sm"
          >
            {showAdvanced ? 'Hide' : 'Show'} advanced (Rate limit, Website, Config JSON)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-2 gap-4 rounded bg-neutral-800/50 p-3">
              <div>
                <label className="text-gray-400 block mb-1">Max Requests / Minute (Optional)</label>
                <input
                  type="number"
                  min={0}
                  value={maxRequestsPerMinute}
                  onChange={(e) => setMaxRequestsPerMinute(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  placeholder="e.g. 60"
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Website URL (Optional)</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="e.g. https://openai.com"
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="text-gray-400 block mb-1">Additional Config JSON (Optional)</label>
                <textarea
                  value={additionalConfigJson}
                  onChange={(e) => setAdditionalConfigJson(e.target.value)}
                  placeholder='e.g. {"headers":{"X-Custom":"value"}}'
                  rows={3}
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none resize-none font-mono text-xs"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-3">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
              className="accent-indigo-500"
            />
            <label className="text-sm text-gray-300">Set status as active</label>
          </div>
        </div>

        {saveError && (
          <p className="flex-shrink-0 px-6 py-2 text-sm text-red-400 border-t border-neutral-800">{saveError}</p>
        )}

        <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950 rounded-b-xl">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSave || saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
          >
            {saving ? 'Saving…' : isEdit ? 'Update Provider' : 'Save Provider'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEmbeddingProviderModal;
